import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, filter, map, mergeMap, of, switchMap } from 'rxjs';
import { ArticleKind, IFolder } from '../../models/article.model';
import { IBundlePosition, PositionKind } from '../../models/position.model';
import { FolderApiService } from '../../services/api/folder.service';
import { IErrorResult } from '../../services/dto.service';
import { GoogleService } from '../../services/utility/google.service';
import { AppState, IFolderState } from '../app.state';
import { CartSelector } from '../cart/cart.selectors';
import { fromCrosssellingActions } from '../crossselling/crossselling.actions';
import { EntityEffects } from '../entity.effects';
import { RouterSelector } from '../router/router.selectors';
import { fromFolderActions } from './folder.actions';
import { FolderSelector } from './folder.selectors';

@Injectable()
export class FolderEffects extends EntityEffects<IFolder, IFolderState> {
	constructor(
		actions$: Actions,
		store: Store<AppState>,
		private folderApiService: FolderApiService,
		private folderSelector: FolderSelector,
		routerSelector: RouterSelector,
		private cartSelector: CartSelector,
		private googleService: GoogleService
	) {
		super(actions$, store, folderApiService, folderSelector, routerSelector, fromFolderActions);
	}

	public onCrosssellingLoad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCrosssellingActions.load),
			debounceTime(10),
			switchMap(() =>
				this.folderApiService.search({ articleKind: ArticleKind.Folder }).pipe(
					map(data => fromCrosssellingActions.loaded({ articles: data })),
					catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onLoad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.load),
			debounceTime(10),
			concatLatestFrom(() => this.store.select(this.folderSelector.paging)),
			switchMap(([, paging]) =>
				this.folderApiService.search({ articleKind: ArticleKind.Folder }, paging).pipe(
					map(data => this.entityActions.loaded({ list: data })),
					catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onLoaded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.loaded),
			filter(({ list }) => list.length > 0),
			concatLatestFrom(() => this.store.select(this.folderSelector.preselected)),
			concatLatestFrom(() => this.store.select(this.cartSelector.selectedPostion)),
			map(([[{ list }, preselected], position]) => {
				let entity = list[0];

				if (position != null && position.positionKind == PositionKind.Bundle) {
					const bundlePosition = position as IBundlePosition;
					const article = bundlePosition.articles.find(x => x.articleKind == ArticleKind.Folder);

					if (article != null) {
						entity = list.find(folder => folder._id == article._id);
					}
				} else if (preselected != null) {
					entity = list.find(folder => folder._id == preselected._id);
				}

				return fromFolderActions.preselected({ entity });
			})
		)
	);

	public onFolderSelectedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromFolderActions.preselected),
				filter(({ entity }) => entity != null),
				mergeMap(({ entity }) => [this.googleService.changeFolder(entity)])
			),
		{ dispatch: false }
	);
}
